import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { SupportChatService } from "@app/wapps/system/support/support-chat.service";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatIconModule } from "@angular/material/icon";
import { Subject, takeUntil } from "rxjs";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { FuseProgressBarService } from "@fuse/components/progress-bar/progress-bar.service";
import { AppAuthService } from "@shared/auth/app-auth.service";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { ConfigurationServiceProxy } from "@api/core";
import { ChangeLeftMenuVisibilityInput, TenantInfo } from "@api/core.models";
import { AppSessionService } from "@vadmin/account/auth";
import { TranslocoCoreModule, brandAsset } from "@i18n";
import { FuseAlertModule } from "@vui/alert";
import { TranscriptionUsageAlertComponent } from "@vhealth/transcription/usage";
import { differenceInCalendarDays } from "date-fns/esm";
import { DisabledWhenExpiredDirective } from "@vadmin/account/auth";

@Component({
    selector: "v2-toolbar",
    templateUrl: "./v2-toolbar.component.html",
    styleUrls: ["./v2-toolbar.component.scss"],
    standalone: true,
    imports: [
        MatToolbarModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        MatIconModule,
        RouterModule,
        TranslocoCoreModule,
        FuseAlertModule,
        DisabledWhenExpiredDirective,
        TranscriptionUsageAlertComponent,
    ],
})
export class V2ToolbarComponent implements OnInit, OnDestroy {
    private _appSession = inject(AppSessionService);

    private _supportChatService = inject(SupportChatService);

    private _fuseProgressBarService = inject(FuseProgressBarService);

    public fuseSplashService = inject(FuseSplashScreenService);

    private _authService = inject(AppAuthService);

    private _fuseSidebarService = inject(FuseSidebarService);

    private _configurationService = inject(ConfigurationServiceProxy);

    private _domSanitizer = inject(DomSanitizer);

    private _unsubscribeAll = new Subject<void>();

    private _router = inject(Router);

    public showAlert = false;

    public showCanceledTrialAlert = false;

    logoUrl: SafeUrl;

    supportChatEnabled: boolean;

    leftMenuCollapsed: boolean;

    ngOnInit(): void {
        this.supportChatEnabled = this._supportChatService.enabled;

        this._appSession.onTenantInfoChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe((val: TenantInfo) => {
            const checkFinancialDebt = abp.setting.getBoolean("System.Payments.EnableDebtLock");
            if (val?.subscriptionInfo?.activeBilling && checkFinancialDebt) {
                let activeBilling = val.subscriptionInfo.activeBilling;
                for (const [key, value] of Object.entries(activeBilling)) {
                    const today = new Date();
                    const dueDate = value;
                    const daysDifference = differenceInCalendarDays(dueDate, today);

                    if (daysDifference <= -30) {
                        this.showAlert = true;
                    }
                }
            }

            const isCanceled = val?.subscriptionInfo?.status?.code === 7;
            const statusDate = val?.subscriptionInfo?.status.date;
            const isInTrial = statusDate && differenceInCalendarDays(statusDate, new Date()) > 0;
            this.showCanceledTrialAlert = isCanceled && !!isInTrial;
            if (isCanceled && !!isInTrial) {
                this.showAlert = false;
            }else if (isCanceled && !isInTrial) {
                this.showAlert = true;
            }

            if (this._appSession.tenantInfo && this._appSession.tenantInfo.logoUrl) {
                this.logoUrl = this._domSanitizer.bypassSecurityTrustUrl(this._appSession.tenantInfo.logoUrl);
            } else {
                this.logoUrl = this._domSanitizer.bypassSecurityTrustUrl(brandAsset("mark"));
            }
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    openKb() {
        window.open("https://help.vsaude.com.br", "_blank");
    }

    openChat() {
        this._supportChatService.showMessengerPanel();
    }

    logout(): void {
        this._fuseProgressBarService.show();
        this._authService.logout(true);
    }

    get canReturnToImpersonate(): boolean {
        return this._appSession.isImpersonating();
    }

    returnToTenants(): void {
        this.fuseSplashService.show();
        this._authService.backToImpersonator();
    }

    toggleSidebarOpened(): void {
        this._fuseSidebarService.getSidebar("main-navbar").toggleOpen();
    }

    toggleSidebarFolded(): void {
        this._fuseSidebarService.getSidebar("main-navbar").toggleFold();
        this.leftMenuCollapsed = !this.leftMenuCollapsed;
        this._configurationService
            .changeLeftMenuVisibility(
                ChangeLeftMenuVisibilityInput.fromJS({
                    collapsed: this._fuseSidebarService.getSidebar("main-navbar").folded,
                })
            )
            .subscribe(() => { });
    }

    navigateToSubscriptionDetails(): void {
        this._router.navigate(["/settings/subscription-details"]);
    }
}
